import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, UserRound } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { accountingApi, type EmployeeAccountingProfile } from '@/lib/api/accounting';
import EmployeeAdvancesTab from '@/components/employees/EmployeeAdvancesTab';

function statusLabel(status?: string): string {
  if (status === 'active') return 'فعال';
  if (status === 'vacation') return 'مرخصی';
  if (status === 'terminated') return 'خاتمه';
  if (status === 'inactive') return 'غیرفعال';
  return status || '—';
}

const EmployeeProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const employeeId = Number(id);

  const [profile, setProfile] = React.useState<EmployeeAccountingProfile | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError('');
    try {
      const data = await accountingApi.employeeAccountingProfile(employeeId);
      setProfile(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'بارگذاری پروفایل کارمند ناموفق بود.');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (loading || !profile) return;
    if (window.location.hash !== '#employee-advances') return;
    const t = window.setTimeout(() => {
      document.getElementById('employee-advances')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [loading, profile]);

  const employee = profile?.employee;

  if (!employeeId) {
    return <p className="text-sm text-rose-600">شناسه کارمند نامعتبر است.</p>;
  }

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="پروفایل کارمند"
        description="مساعده، علی‌الحساب و پرداخت‌های ثبت‌شده برای این شخص"
        actions={
          <Button asChild variant="outline" className="gap-2">
            <Link to="/employees">
              <ArrowRight className="h-4 w-4" />
              بازگشت به لیست
            </Link>
          </Button>
        }
      />

      <LoadingLock loading={loading}>
        {error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {employee ? (
          <>
            <Card className="overflow-hidden rounded-xl shadow-sm ring-1 ring-slate-200/60">
              <CardHeader className="border-b border-slate-200/80 bg-gradient-to-r from-sky-50 to-indigo-50 px-6 py-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-sky-700 shadow-sm ring-1 ring-sky-100">
                      <UserRound className="h-7 w-7" />
                    </span>
                    <div>
                      <CardTitle className="text-xl font-semibold text-slate-900">
                        {employee.full_name || `${employee.first_name} ${employee.last_name}`.trim()}
                      </CardTitle>
                      <CardDescription className="mt-1 space-y-0.5 text-sm">
                        <div>
                          کد پرسنلی: {employee.employee_code || '—'} · کد ملی: {employee.national_code || '—'}
                        </div>
                        <div>
                          موبایل: {employee.mobile || '—'} · وضعیت: {statusLabel(employee.status)}
                        </div>
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            <EmployeeAdvancesTab employeeId={employeeId} />
          </>
        ) : null}
      </LoadingLock>
    </div>
  );
};

export default EmployeeProfilePage;
